import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './split-reservation-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';

export interface SplitReservationModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservation: ReservationDetailsUiModel | undefined;
    isLoading: boolean;
    errorMessage?: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    submitHandler: (checkInDate?: number, checkOutDate?: number) => void;
  };
}

/**
 * functional component SplitReservationModal
 *
 * Lets the reservation team carve off the days the guest won't actually stay in
 * the unit (e.g. delayed CFC access) and turns those days into a block date,
 * while the reservation itself keeps only the real stay dates.
 */
export const SplitReservationModal: React.FC<SplitReservationModalProps> = ({
  config,
  data,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();

  const [newCheckInDate, setNewCheckInDate] = useState<Date | null>(null);
  const [newCheckOutDate, setNewCheckOutDate] = useState<Date | null>(null);

  const originalCheckIn = data.reservation ? new Date(data.reservation.checkInDate) : null;
  const originalCheckOut = data.reservation ? new Date(data.reservation.checkOutDate) : null;

  const hasChange = newCheckInDate !== null || newCheckOutDate !== null;
  const effectiveCheckIn = newCheckInDate || originalCheckIn;
  const effectiveCheckOut = newCheckOutDate || originalCheckOut;
  const isRangeValid =
    !!effectiveCheckIn && !!effectiveCheckOut && effectiveCheckIn < effectiveCheckOut;
  const isWithinOriginalRange =
    (!newCheckInDate || (!!originalCheckIn && newCheckInDate >= originalCheckIn)) &&
    (!newCheckOutDate || (!!originalCheckOut && newCheckOutDate <= originalCheckOut));

  const isValid = hasChange && isRangeValid && isWithinOriginalRange;

  const resetAndClose = () => {
    setNewCheckInDate(null);
    setNewCheckOutDate(null);
    eventHandlers.closeHandler();
  };

  const handleSubmit = () => {
    if (!isValid) return;
    eventHandlers.submitHandler(
      newCheckInDate ? newCheckInDate.getTime() : undefined,
      newCheckOutDate ? newCheckOutDate.getTime() : undefined
    );
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{
        closeModalHandler: resetAndClose
      }}
    >
      <div className={styles['split-reservation-modal']}>
        {/* Header */}
        <div className={styles['split-reservation-modal__header']}>
          <h2 className={styles['split-reservation-modal__title']}>Shorten Reservation</h2>
          <p className={styles['split-reservation-modal__subtitle']}>
            Carve off the days the guest won&apos;t stay in the unit as a block date.
          </p>
        </div>

        <div className={styles['split-reservation-modal__warning']}>
          <span className={styles['split-reservation-modal__warning-icon']}>&#9888;</span>
          The remaining day(s) will be marked as a block date and removed from this reservation.
        </div>

        {/* Current Dates */}
        {data.reservation && (
          <div className={styles['split-reservation-modal__current']}>
            <div className={styles['split-reservation-modal__current-label']}>Current Dates</div>
            <div className={styles['split-reservation-modal__current-dates']}>
              <span>
                {data.reservation && new Date(data.reservation.checkInDate).toDateString()}
              </span>
              <span className={styles['split-reservation-modal__arrow-icon']}>&#8594;</span>
              <span>
                {data.reservation && new Date(data.reservation.checkOutDate).toDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Date Pickers */}
        <div className={styles['split-reservation-modal__content']}>
          <div className={styles['split-reservation-modal__field']}>
            <label className={styles['split-reservation-modal__label']}>
              New Check-in Date{' '}
              <span className={styles['split-reservation-modal__hint']}>
                (leave empty to keep current)
              </span>
            </label>
            <ReactDatePicker
              selected={newCheckInDate}
              onChange={(date: Date) => setNewCheckInDate(date)}
              minDate={originalCheckIn || undefined}
              maxDate={
                newCheckOutDate || originalCheckOut
                  ? new Date((newCheckOutDate || originalCheckOut)!.getTime() - 86400000)
                  : undefined
              }
              isClearable
              placeholderText="Keep current check-in date"
              dateFormat="dd/MM/yyyy"
              className={styles['split-reservation-modal__datepicker']}
              wrapperClassName={styles['split-reservation-modal__datepicker-wrapper']}
              popperPlacement="bottom-start"
            />
          </div>

          <div className={styles['split-reservation-modal__field']}>
            <label className={styles['split-reservation-modal__label']}>
              New Check-out Date{' '}
              <span className={styles['split-reservation-modal__hint']}>
                (leave empty to keep current)
              </span>
            </label>
            <ReactDatePicker
              selected={newCheckOutDate}
              onChange={(date: Date) => setNewCheckOutDate(date)}
              minDate={
                newCheckInDate || originalCheckIn
                  ? new Date((newCheckInDate || originalCheckIn)!.getTime() + 86400000)
                  : undefined
              }
              maxDate={originalCheckOut || undefined}
              isClearable
              placeholderText="Keep current check-out date"
              dateFormat="dd/MM/yyyy"
              className={styles['split-reservation-modal__datepicker']}
              wrapperClassName={styles['split-reservation-modal__datepicker-wrapper']}
              popperPlacement="bottom-start"
            />
          </div>
        </div>

        {/* Inline Error */}
        {data.errorMessage && (
          <div className={styles['split-reservation-modal__error']}>
            <span className={styles['split-reservation-modal__error-icon']}>&#9888;</span>
            {data.errorMessage}
          </div>
        )}

        {/* Actions */}
        <div className={styles['split-reservation-modal__actions']}>
          <Button
            onClick={resetAndClose}
            type={ButtonType.primaryInverted}
            tracking={{
              action: 'close',
              category: 'reservation',
              label: 'split-reservation-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            loading={data.isLoading}
            disabled={!isValid || data.isLoading}
            onClick={handleSubmit}
            tracking={{
              action: 'submit',
              category: 'reservation',
              label: 'split-reservation-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            {data.isLoading ? 'Splitting...' : 'Split Reservation'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
